/**
 * CW-SF-D1 TKT-5 — Stalled Deals widget.
 *
 * Open opps with no LastModifiedDate change in 14+ days. Sorted
 * oldest first. days-stalled chip styled yellow at 14-30d, red 30d+.
 */
import { useCallback } from 'react';
import { ExternalLink, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface StalledItem {
  id: string;
  external_file_id: string;
  name: string;
  web_url: string | null;
  stage: string | null;
  amount: number | null;
  days_stalled: number | null;
  owner_id: string | null;
  owner_name: string | null;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: StalledItem[];
  stalled_days: number;
}

const REFRESH_S = 5 * 60;

function stalledChipClass(days: number | null): string {
  if (days === null) return 'sf-widget__chip';
  if (days >= 30) return 'sf-widget__chip sf-widget__chip--stalled-red';
  return 'sf-widget__chip sf-widget__chip--stalled-yellow';
}

export function SfStalledDealsWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-stalled-deals', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--stalled">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage={`No team opps stalled beyond ${data?.stalled_days ?? 14} days.`}
      >
        <ul className="sf-widget__list">
          {data?.items.map((o) => (
            <li key={o.id} className="sf-widget__row">
              <AlertTriangle size={12} className="sf-widget__row-icon" aria-hidden="true" />
              <a
                href={o.web_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sf-widget__row-name"
              >
                {o.name}
                <ExternalLink size={10} />
              </a>
              <div className="sf-widget__row-chips">
                <span className={stalledChipClass(o.days_stalled)}>
                  {o.days_stalled ?? '—'}d stalled
                </span>
                {o.stage && <span className="sf-widget__chip">{o.stage}</span>}
                {o.amount !== null && (
                  <span className="sf-widget__chip sf-widget__chip--amount">
                    {formatCurrency(o.amount)}
                  </span>
                )}
                {o.owner_name && (
                  <span className="sf-widget__chip sf-widget__chip--owner">
                    {o.owner_name}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
